import _ from 'lodash';
import moment from 'moment';
import { DATE_FORMATS } from '../../../utils/dateTime';

export type validationInputFunction = (input: string) => boolean;
export type validationValueFunction = (value: any) => boolean;
export type validationFunction =
  | validationInputFunction
  | validationValueFunction;

const EMAIL_REGEX =
  // eslint-disable-next-line max-len, no-control-regex
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
const POSTALCODE_REGEX = /(^\d{5}-\d{4}$|^\d{5}$)/;

const PASSWORD_SPECIAL_CHARACTERS = /[@~`! @#$%^&*()_=+\\';:"\\/?>. <,-]+/;

export class Validations {
  static isNotEmpty(value: string | undefined | null): boolean {
    return value !== undefined && value !== null && String(value).trim() !== '';
  }

  static isMinLength(length = 2): validationInputFunction {
    return (value: string): boolean => {
      return (
        Validations.isNotEmpty(value) && String(value).trim().length >= length
      );
    };
  }

  static isExactLength(length = 2): validationInputFunction {
    return (value: string): boolean => {
      return (
        Validations.isNotEmpty(value) &&
        value.length === length &&
        value.length === String(value).trim().length
      );
    };
  }

  static isUnderMaxLength(length = 2): validationInputFunction {
    return (value: string): boolean => {
      return Validations.isNotEmpty(value) && value.length < length;
    };
  }

  static isValidEmail(value: string): boolean {
    return Validations.isNotEmpty(value) && EMAIL_REGEX.test(value);
  }

  static isValidPassword(value: string) {
    return (
      Validations.isNotEmpty(value) &&
      Validations.isMinLength(8)(value) &&
      PASSWORD_SPECIAL_CHARACTERS.test(value) &&
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value)
    );
  }

  static isValidDate(
    dateObj: { day: string; month: string; year: string },
    format?: string,
  ): boolean {
    const dateObjKeys = Object.keys(dateObj);
    if (
      !dateObj ||
      ['day', 'month', 'year'].every((k) => !dateObjKeys.includes(k))
    )
      return false;
    const value = `${dateObj.month}-${dateObj.day}-${dateObj.year}`;
    return (
      Validations.isNotEmpty(value) &&
      moment(value, format || DATE_FORMATS.M_D_YYYY_V2, true).isValid()
    );
  }

  static isMinYearsAgo(years: number): validationInputFunction {
    return (value: string): boolean => {
      const date = moment(value, true);
      return moment().diff(date, 'years') > years;
    };
  }

  static isMaxYearsAgo(years: number): validationInputFunction {
    return (value: string): boolean => {
      const date = moment(value, true);
      return moment().diff(date, 'years') < years;
    };
  }

  static isUnderMaxValue(maxValue: number): validationInputFunction {
    return (value: string): boolean => {
      return Validations.isNotEmpty(value) && Number(value) < maxValue;
    };
  }

  static isOverMinValue(minValue: number): validationInputFunction {
    return (value: string): boolean => {
      return Validations.isNotEmpty(value) && Number(value) > minValue;
    };
  }

  static isPositiveNumber(value: string): boolean {
    return Validations.isOverMinValue(0)(value);
  }

  static isValidPostalCode(value: string): boolean {
    return POSTALCODE_REGEX.test(value);
  }

  static isDistinct(test: any): validationValueFunction {
    return (value: any): boolean => {
      return !_.isEqual(test, value);
    };
  }

  static isValidBirthYear(value: string): boolean {
    return (
      Validations.isMinYearsAgo(12)(value) &&
      Validations.isOverMinValue(1900)(value)
    );
  }

  static isValidBirthDate(value: string): boolean {
    if (!value) return !value;
    const [month, day, year] = value.split('/');
    return (
      Validations.isValidDate({ month, day, year }) &&
      Validations.isValidBirthYear(year)
    );
  }
}
